import { ReactNode, lazy, Suspense, useState, useEffect } from 'react';

// Clerk keys by environment
const CLERK_PROD_KEY = "pk_live_Y2xlcmsuZWxldmFkdmlzb3IuY29tJA";
const CLERK_DEV_KEY = "pk_test_aW50ZXJuYWwtY2hpbXAtOTMuY2xlcmsuYWNjb3VudHMuZGV2JA";

// Auto-select based on hostname
const isProduction = typeof window !== 'undefined' && 
  (window.location.hostname === 'elevadvisor.com' || 
   window.location.hostname === 'www.elevadvisor.com');

const CLERK_PUBLISHABLE_KEY = isProduction ? CLERK_PROD_KEY : CLERK_DEV_KEY;

// Detect bots/crawlers to avoid unnecessary Clerk API calls
const isBot = typeof navigator !== 'undefined' && (
  /bot|crawl|spider|lighthouse|headless|phantom|puppeteer|selenium/i.test(navigator.userAgent) ||
  !navigator.userAgent
);

// Check if we're on a page that needs Clerk immediately (auth pages, dashboard)
const needsClerkImmediately = typeof window !== 'undefined' && (
  window.location.pathname.startsWith('/members') ||
  window.location.pathname.startsWith('/dashboard') ||
  window.location.pathname.startsWith('/admin') ||
  window.location.pathname.startsWith('/auth')
);

// Lazy load @clerk/clerk-react to keep it out of the critical rendering path.
const ClerkProviderLazy = lazy(() =>
  import('@clerk/clerk-react').then(mod => ({
    default: mod.ClerkProvider,
  }))
);

interface ClerkProviderWrapperProps {
  children: ReactNode;
}

export function ClerkProviderWrapper({ children }: ClerkProviderWrapperProps) {
  // On non-auth pages, delay Clerk loading to reduce main-thread work during LCP window
  const [shouldLoadClerk, setShouldLoadClerk] = useState(needsClerkImmediately);

  useEffect(() => {
    if (shouldLoadClerk || isBot) return;
    
    // Defer Clerk loading 3s on non-auth pages to keep main thread free
    const id = setTimeout(() => setShouldLoadClerk(true), 3000);
    return () => clearTimeout(id);
  }, [shouldLoadClerk]);

  // Skip Clerk for bots/crawlers to prevent 400 errors in Lighthouse
  if (isBot) {
    return <>{children}</>;
  }

  if (!CLERK_PUBLISHABLE_KEY) {
    console.error("Missing Clerk Publishable Key");
    return <>{children}</>;
  }

  if (!shouldLoadClerk) {
    return <>{children}</>;
  }

  return (
    <Suspense fallback={children}>
      <ClerkProviderLazy publishableKey={CLERK_PUBLISHABLE_KEY}>
        {children}
      </ClerkProviderLazy>
    </Suspense>
  );
}
